import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, PlusCircle, Search, Filter, Edit2, Trash2, Eye, AlertCircle, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown, Heart } from 'lucide-react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_MY_TOOLS_MANAGE } from '../graphql/queries/myToolsManage';
import { DELETE_TOOL } from '../graphql/mutations/deleteTool';
import { useAuth } from '../context/AuthContext';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import toast from 'react-hot-toast';

const ManageTools: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState<'all' | 'AVAILABLE' | 'LOANED'>('all');
  const [sortBy, setSortBy] = useState<'NAME' | 'STATUS' | 'PRICE' | 'VIEWS'>('NAME');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const { data: myToolsData, loading, error, refetch } = useQuery(GET_MY_TOOLS_MANAGE, { 
    skip: !user, 
    fetchPolicy: "no-cache",
    variables: {
      search: searchQuery || undefined,
      availability: availabilityFilter !== 'all' ? availabilityFilter : undefined,
      sortBy,
      sortDirection
    }
  });

  const [deleteTool] = useMutation(DELETE_TOOL);

  const userTools: any[] = myToolsData?.myTools?.items || [];
  const disabledToolCount = userTools.filter((tool) => tool.disabled).length;
  
  // Check if tool has an active booking that prevents deletion
  const hasActiveBooking = (tool: any) => {
    if (!tool.nextToolBooking) return false;
    const activeStatuses = ['requested', 'confirmed', 'picked_up'];
    return activeStatuses.includes(tool.nextToolBooking.status);
  };
  
  const handleDelete = async (toolId: string) => {
    const tool = userTools.find(t => t.id === toolId);
    if (!tool) return;
    
    // Check if tool has active bookings
    if (hasActiveBooking(tool)) {
      toast.error('Værktøjet kan ikke slettes, da det har en aktiv booking.');
      setShowDeleteConfirm(null);
      return;
    }
    
    setIsDeleting(true);
    try {
      const result = await deleteTool({
        variables: { id: toolId }
      });
      
      if (result.data?.deleteTool?.tool?.id) {
        toast.success('Værktøj slettet succesfuldt');
        setShowDeleteConfirm(null);
        // Refetch the tools list
        await refetch();
      } else {
        const errors = result.data?.deleteTool?.errors || ['Ukendt fejl'];
        toast.error(`Kunne ikke slette værktøj: ${errors.join(', ')}`);
      }
    } catch (error: any) {
      console.error('Failed to delete tool:', error);
      toast.error(error.message || 'Der opstod en fejl ved sletning af værktøj');
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleSort = (column: 'NAME' | 'STATUS' | 'PRICE' | 'VIEWS') => {
    if (sortBy === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      // New column, default to ASC
      setSortBy(column);
      setSortDirection('ASC');
    }
  };

  const getSortIcon = (column: 'NAME' | 'STATUS' | 'PRICE' | 'VIEWS') => {
    if (sortBy !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-30" />;
    }
    return sortDirection === 'ASC' 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  // Helper function to get status badge styling and text
  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { text: string; color: string }> = {
      requested: { text: 'Anmodet', color: 'bg-yellow-100 text-yellow-800' },
      declined: { text: 'Afvist', color: 'bg-red-100 text-red-800' },
      canceled: { text: 'Annulleret', color: 'bg-gray-100 text-gray-800' },
      confirmed: { text: 'Bekræftet', color: 'bg-blue-100 text-blue-800' },
      picked_up: { text: 'Afhentet', color: 'bg-green-100 text-green-800' },
      returned: { text: 'Returneret', color: 'bg-purple-100 text-purple-800' },
      reviewed_by_booking_user: { text: 'Anmeldt af lejer', color: 'bg-indigo-100 text-indigo-800' },
      reviewed_by_owner: { text: 'Anmeldt af ejer', color: 'bg-indigo-100 text-indigo-800' },
      completed: { text: 'Afsluttet', color: 'bg-gray-100 text-gray-800' },
    };
    return statusMap[status] || { text: status, color: 'bg-gray-100 text-gray-800' };
  };

  // Helper function to map status to Badge variant
  const getStatusVariant = (status: string): 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'featured' => {
    const variantMap: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'featured'> = {
      requested: 'warning',
      declined: 'danger',
      canceled: 'secondary',
      confirmed: 'featured',
      picked_up: 'success',
      returned: 'primary',
      reviewed_by_booking_user: 'featured',
      reviewed_by_owner: 'featured',
      completed: 'secondary',
    };
    return variantMap[status] || 'secondary';
  };

  return (
    <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <button onClick={() => navigate(-1)} className="flex items-center text-green-600 mb-4 hover:text-green-700">
          <ArrowLeft className="h-5 w-5 mr-2" />
          <span>Tilbage til dashboard</span>
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-gray-900 mb-4 sm:mb-0">Administrér værktøj</h1>
          <Link to="/add-tool">
            <Button variant="primary" icon={<PlusCircle className="h-4 w-4 mr-2" />}>
              Tilføj nyt værktøj
            </Button>
          </Link>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <p className="text-sm text-red-600">Der opstod en fejl ved indlæsning af værktøj.</p>
          </div>
        </div>
      )}

      {!loading && !error && (
        <>
      
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-8">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-grow">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Søg i værktøj..."
                value={localSearchQuery}
                onChange={(e) => setLocalSearchQuery(e.target.value)}
                onBlur={() => setSearchQuery(localSearchQuery)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearchQuery(localSearchQuery);
                    e.currentTarget.blur();
                  }
                }}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm"
              />
            </div>
          </div>
          
          {/* Status Filter */}
          <div className="flex items-center space-x-4">
            <Filter className="h-5 w-5 text-gray-400" />
            <button
              onClick={() => setAvailabilityFilter('all')}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                availabilityFilter === 'all'
                  ? 'bg-green-100 text-green-800'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Alle
            </button>
            <button
              onClick={() => setAvailabilityFilter('AVAILABLE')}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                availabilityFilter === 'AVAILABLE'
                  ? 'bg-green-100 text-green-800'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Tilgængelig
            </button>
            <button
              onClick={() => setAvailabilityFilter('LOANED')}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                availabilityFilter === 'LOANED'
                  ? 'bg-green-100 text-green-800'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Udlånt
            </button>
          </div>
        </div>
      </div>
      
      {/* Tools List */}
      {userTools.length > 0 ? (
        <div>
          {disabledToolCount > 0 && (
            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-amber-700 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  {disabledToolCount} {disabledToolCount === 1 ? 'værktøj er deaktiveret' : 'værktøj er deaktiveret'}.
                  {' '}Deaktiveret værktøj kan ikke fremsøges eller bookes.
                </p>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('NAME')}
                  >
                    <div className="flex items-center">
                      Værktøj
                      {getSortIcon('NAME')}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('STATUS')}
                  >
                    <div className="flex items-center">
                      Status
                      {getSortIcon('STATUS')}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('PRICE')}
                  >
                    <div className="flex items-center">
                      Pris
                      {getSortIcon('PRICE')}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('VIEWS')}
                  >
                    <div className="flex items-center">
                      Visninger
                      {getSortIcon('VIEWS')}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Favoritter
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Handlinger
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {userTools.map((tool) => (
                  <tr key={tool.id} className={tool.disabled ? 'bg-amber-50/40' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          {tool.primaryImageUrl ? (
                            <img
                              src={tool.primaryImageUrl}
                              alt={tool.name}
                              className="h-10 w-10 rounded object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded bg-gray-200 flex items-center justify-center">
                              <span className="text-gray-400 text-xs">Ingen</span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                            <span>{tool.name}</span>
                            {tool.disabled && (
                              <Badge variant="warning">Deaktiveret</Badge>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{tool.city}, {tool.postalCode}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {tool.disabled ? (
                        <div title="Værktøjet er deaktiveret" className="cursor-help">
                          <Badge variant="warning">Deaktiveret</Badge>
                        </div>
                      ) : tool.nextToolBooking ? (
                        <div 
                          title={`${new Date(tool.nextToolBooking.fromDate).toLocaleDateString('da-DK')} - ${new Date(tool.nextToolBooking.toDate).toLocaleDateString('da-DK')}`}
                          className="cursor-help"
                        >
                          <Badge variant={getStatusVariant(tool.nextToolBooking.status)}>
                            {getStatusBadge(tool.nextToolBooking.status).text}
                          </Badge>
                        </div>
                      ) : (
                        <Badge variant="success">
                          Ledig
                        </Badge>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{tool.pricePerDayCents / 100} kr./dag</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <Eye className="h-4 w-4 mr-1" />
                        <span>{tool.viewCount || 0}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <Heart className="h-4 w-4 mr-1" />
                        <span>{tool.favouriteCount || 0}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-2">
                        <Link to={`/tool/${tool.id}`}>
                          <Button variant="outline" size="sm" icon={<Eye className="h-4 w-4" />}>
                            Se
                          </Button>
                        </Link>
                        <Link to={`/edit-tool/${tool.id}`}>
                          <Button variant="outline" size="sm" icon={<Edit2 className="h-4 w-4" />}>
                            Rediger
                          </Button>
                        </Link>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          icon={<Trash2 className="h-4 w-4" />}
                          onClick={() => setShowDeleteConfirm(tool.id)}
                          disabled={hasActiveBooking(tool)}
                          title={hasActiveBooking(tool) ? 'Værktøjet kan ikke slettes da det har en aktiv booking' : 'Slet værktøj'}
                        >
                          Slet
                        </Button>
                      </div>
                      
                      {/* Delete Confirmation Modal */}
                      {showDeleteConfirm === tool.id && (
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
                          <div className="bg-white rounded-lg p-6 max-w-sm w-full whitespace-normal">
                            <div className="flex items-center justify-center text-red-600 mb-4">
                              <AlertCircle className="h-12 w-12" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                              Slet værktøj
                            </h3>
                            <p className="text-sm text-gray-500 mb-6 whitespace-normal break-words leading-relaxed">
                              Er du sikker på, at du vil slette "{tool.name}"? Denne handling kan ikke fortrydes.
                            </p>
                            <div className="flex justify-end space-x-4">
                              <Button
                                variant="outline"
                                onClick={() => setShowDeleteConfirm(null)}
                                disabled={isDeleting}
                              >
                                Annuller
                              </Button>
                              <Button
                                variant="primary"
                                className="bg-red-600 hover:bg-red-700"
                                onClick={() => handleDelete(tool.id)}
                                isLoading={isDeleting}
                              >
                                Slet
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Intet værktøj fundet</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchQuery || availabilityFilter !== 'all'
              ? 'Prøv at justere din søgning eller filtre'
              : 'Kom i gang ved at tilføje dit første værktøj'}
          </p>
          <div className="mt-6">
            <Link to="/add-tool">
              <Button variant="primary" icon={<PlusCircle className="h-4 w-4 mr-2" />}>
                Tilføj nyt værktøj
              </Button>
            </Link>
          </div>
        </div>
      )}
        </>
      )}
      
    </div>
  );
};

export default ManageTools;